<script setup>
  import { ref, computed, useTemplateRef, watchEffect, onMounted, onUnmounted } from 'vue'
  import { useUserStore } from "@/stores/UserStore.js";
  import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore';

  import { useCookie } from "@/composables/useCookie.js";
  import CloseButton from "@/components/CloseButton.vue";
  import Icon from "@/components/icons/Icon.vue";

  const messagesContainer = useTemplateRef('messages-container');

  const userStore = useUserStore();
  const googleAnalyticsStore = useGoogleAnalyticsStore();
  const cookie = useCookie();

  const cookieName = `msg-${userStore.messages[0].Id}-dismissed`;
  const messageExpiration = new Date(userStore.messages[0].Expire_Time).toUTCString();
  const messageType = parseInt(userStore.messages[0].Type);

  const hideMessage = ref(cookie.get(cookieName) === 'true');
  const messageLineCount = ref(0);

  const message = computed(() => `<span data-message-marker="start"></span>${userStore.messages[0].Message}<span data-message-marker="end"></span>`);
  const iconName = computed(() => messageType === 401 ? 'premium-badge' : messageType === 402 ? 'stopwatch' : false);
  const messageClasses = computed(() => ['jwx-map-message', {'jwx-map-message--icon': iconName.value}]);

  cookie.set(cookieName, hideMessage.value, { expires: messageExpiration });

  const attachedAnalyticsToMessageLinks = (links) => {
    if (!links) return;
      
    links.forEach(link => {
      const url = new URL(link.href);

      link.addEventListener('click', (event) => {       
        event.preventDefault(); 

        googleAnalyticsStore.sendEvent("Message_Link_Click", {
            "Hostname": url.hostname,
            "Pathname": url.pathname,
            "Search": url.search,
          });

          window.location = link.href;
      });
    });
  }

  const setMessageLineCount = (markers) => {
    // Message line count is used to determine style of message container
    // If the message is multiline, we will style it differently
    if (!markers) return;

    let messageStartMarkerRect = null;
    let messageEndMarkerRect = null;

    markers.forEach(marker => {
      if (marker.dataset.messageMarker === 'start') {
        messageStartMarkerRect = marker.getBoundingClientRect();
      } else if (marker.dataset.messageMarker === 'end') {
        messageEndMarkerRect = marker.getBoundingClientRect();
      }
    });

    messageLineCount.value = (messageEndMarkerRect.bottom - messageStartMarkerRect.top) / messageStartMarkerRect.height;    
  }

  watchEffect(() => cookie.set(cookieName, hideMessage.value, { expires: messageExpiration }));

  onMounted(() => {
      const messageLinks = messagesContainer.value.querySelectorAll('.jwx-map-message__text a');
      const messageMarkers = messagesContainer.value.querySelectorAll('.jwx-map-message__text [data-message-marker], .jwx-map-message__text [data-message-marker]');

      attachedAnalyticsToMessageLinks(messageLinks);
      setMessageLineCount(messageMarkers);

      window.addEventListener('resize', () => { setMessageLineCount(messageMarkers); });
  });

  onUnmounted(() => {
    window.removeEventListener('resize', () => { setMessageLineCount(); });
  });

</script>

<template>
  <div v-if="!hideMessage" class="jwx-map__messages">
    <div :class="messageClasses" :data-message-type="userStore.messages[0].Type" :data-multiline="messageLineCount > 1">
      <Icon v-if="iconName" :name="iconName" class="jwx-map-message__icon" />
      <p ref="messages-container" v-html="message" class="jwx-map-message__text"></p>
      <CloseButton
        @click.prevent="hideMessage = true"
        class="jwx-map-message__dismiss"
        type="button" 
      />
    </div>
  </div>
</template>